import { useCallback, useEffect, useState } from "react";
import { Check, Loader2, ScanFace, ShieldCheck } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { PrimaryButton, SecondaryButton } from "@/components/shared/ui";
import { StepActions } from "@/components/applicant/QuestionStep";
import { ErrorBox, PassBox } from "@/components/applicant/stageUi";
import { LaterLink } from "@/components/applicant/DocumentItem";

const POLL_MS = 3000;

/** The applicant's own ID: a live Stripe Identity check (ID scan + selfie) instead of an upload. */
export const IdentityItem = ({ item, token, onUpdated, onContinue, onLater, compact }) => {
  const [identity, setIdentity] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.post(`/a/${token}/identity/refresh`);
      setIdentity(data.identity || {});
      if (["verified", "failed"].includes(data.identity?.status)) await onUpdated();
    } catch (err) {
      setError(errorText(err));
    }
  }, [token, onUpdated]);

  useEffect(() => {
    refresh();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const status = identity?.status;
  const waiting = status === "processing" || (status === "started" && new URLSearchParams(window.location.search).has("identity"));
  useEffect(() => {
    if (!waiting) return undefined;
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
  }, [waiting, refresh]);

  const start = async () => {
    setBusy(true);
    setError("");
    try {
      window.location.assign((await api.post(`/a/${token}/identity/start`)).data.url);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  const done = status === "verified" || status === "failed";
  const retry = status === "started" || status === "failed";
  const actions = [
    status !== "verified" && <PrimaryButton key="v" size="lg" onClick={start} isLoading={busy} disabled={!identity} testid="identity-start">
      {!busy && <ScanFace />} {retry ? "Try the ID check again" : "Verify my ID"}
    </PrimaryButton>,
    done && onContinue && (status === "failed"
      ? <SecondaryButton key="c" size="lg" onClick={onContinue} testid="identity-continue">Continue</SecondaryButton>
      : <PrimaryButton key="c" size="lg" onClick={onContinue} testid="identity-continue">Continue</PrimaryButton>),
    !done && onLater && <LaterLink key="l" onClick={onLater} testid="item-later-photo_id" />,
  ].filter(Boolean);

  return (
    <div className="rise" data-testid="checklist-item-photo_id" data-status={status || "loading"}>
      {status === "verified" && <span className="mb-3 inline-flex items-center gap-1 rounded-full bg-success/10 px-2.5 py-1 text-xs font-medium text-success" data-testid="item-accepted-photo_id"><Check className="size-3" strokeWidth={3} /> Verified</span>}
      <h1 className={compact ? "text-xl font-semibold leading-tight" : "text-[26px] font-semibold leading-tight tracking-tight"} data-testid="item-title-photo_id">{item.title}</h1>
      <p className="mt-2 text-base leading-relaxed text-muted-foreground">
        A quick live check with Stripe: scan your passport or photo driving licence, then take a selfie so we know it's really you. It takes about 2 minutes.
      </p>
      <div className="mt-6 flex items-start gap-3 rounded-xl bg-background p-4 ring-1 ring-border">
        <ShieldCheck className="mt-0.5 size-5 shrink-0 text-success" />
        <p className="text-sm leading-relaxed">The agent only sees your name and whether the check passed. Your photos and ID number stay with Stripe.</p>
      </div>
      {!identity && !error && <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin" /> Loading…</p>}
      {waiting && <p className="mt-4 flex items-center gap-2 rounded-xl bg-brand/40 p-4 text-sm font-medium dark:bg-brand/10" data-testid="identity-checking"><Loader2 className="size-4 animate-spin" /> Checking your ID…</p>}
      {status === "verified" && <div className="mt-4"><PassBox testid="identity-verified">Your ID is verified.</PassBox></div>}
      {status === "failed" && (
        <ErrorBox className="mt-4" testid="identity-failed">
          We couldn't verify your ID. {identity.error} You can try again. Until a check passes, the agent sees a failed ID check.
        </ErrorBox>
      )}
      {error && <ErrorBox className="mt-4" testid="identity-error">{error}</ErrorBox>}
      {actions.length > 0 && (compact ? <div className="mt-6 flex flex-col gap-2">{actions}</div> : <StepActions>{actions}</StepActions>)}
    </div>
  );
};
